import moment from "moment";
import * as XLSX from "xlsx-js-style";
import { border, headerStyle, numeroStyle, textStyle } from "../../../../Components/ExcelEstilos";

/**
 * La plantilla de "Importar Empleados", como única fuente de verdad del formato.
 *
 * La usan dos puntas que tienen que coincidir celda por celda:
 *   - `ImportarEmpleados` → "Descargar Plantilla" (una fila de ejemplo) y la lectura del archivo.
 *   - `ModalExportarEmpleados` → el Excel con los empleados reales de una empresa, pensado para
 *     subirse tal cual con "Importar Empleados" en otra empresa.
 *
 * Por eso los dos escriben con `construirLibroPlantilla`. Si se agrega una columna aquí, hay que
 * leerla en `archivoVerificar` de `ImportarEmpleados.js`: el importador lee por encabezado.
 *
 * Módulo sin React ni antd a propósito, para poder probarlo con un `node` desechable.
 */

export const NOMBRE_HOJA_PLANTILLA = "Empleados";

// El importador relee el texto ya formateado de cada celda (`raw: false`), así que el formato de
// dinero NO puede ser el de Contabilidad del resto del proyecto: ese pinta el 0 como "$ -", que
// `checarNumero` convierte en NaN y la fila completa se rechaza (todo Salario Variable en 0).
const FORMATO_DINERO = "#,##0.00";

const estiloDinero = { ...numeroStyle, numFmt: FORMATO_DINERO };
const estiloCentro = { border, alignment: { horizontal: "center", vertical: "center" } };

const ESTADO_CIVIL = {
  1: "Soltero",
  2: "Casado",
  3: "Concubinato",
  4: "Unión Civil",
  5: "Viudo",
  6: "Divorciado",
};

// Mismo enum para basePago y baseDeCotizacion.
const BASE = {
  1: "Sueldo",
  2: "Comisión",
  3: "Destajo",
  4: "Sueldo-Comisión",
  5: "Sueldo-Destajo",
};

const campo = (ref, nombre = "nombre") => (ref && typeof ref === "object" ? ref[nombre] : undefined);

// Texto DD/MM/AAAA en UTC: una fecha-día puede estar guardada a medianoche UTC (importaciones) y
// en hora local se correría al día anterior. El 30/12/1899 es la fecha cero de Excel: vacía.
const fecha = (valor) => {
  if (!valor) return undefined;
  const m = moment.utc(valor);
  return m.isValid() && m.year() >= 1900 ? m.format("DD/MM/YYYY") : undefined;
};

const siNo = (valor) => (valor ? "SI" : "NO");

/**
 * tipo: texto | entero | dinero | fecha | siNo | centro (texto centrado)
 * valor(e): del empleado poblado (ver `exportarEmpleados` en el backend) a lo que el importador
 * sabe leer. Los catálogos van por descripción o nombre, nunca por id, porque se resuelven de
 * nuevo en la empresa destino.
 */
export const COLUMNAS_PLANTILLA = [
  // Identificación
  { encabezado: "Numero", tipo: "entero", valor: (e) => e.codigo },
  { encabezado: "Nombre", valor: (e) => e.nombre },
  { encabezado: "Apellido Paterno", valor: (e) => e.apellidoPaterno },
  { encabezado: "Apellido Materno", valor: (e) => e.apellidoMaterno },
  { encabezado: "RFC", valor: (e) => e.rfc },
  { encabezado: "CURP", ancho: 20, valor: (e) => e.curp },
  { encabezado: "NSS", valor: (e) => e.numSeguridadSocial },
  // Fechas
  { encabezado: "Fecha Alta", tipo: "fecha", valor: (e) => fecha(e.fechaAlta) },
  { encabezado: "Fecha Reingreso", tipo: "fecha", valor: (e) => fecha(e.fechaReingreso) },
  { encabezado: "Fecha Nacimiento", tipo: "fecha", valor: (e) => fecha(e.fechaNacimiento) },
  // Datos personales. Sexo va con la palabra: `mapearSexo` lee la "M" como Hombre.
  { encabezado: "Sexo", tipo: "centro", valor: (e) => e.sexo },
  { encabezado: "Estado Civil", valor: (e) => ESTADO_CIVIL[e.estadoCivil] },
  { encabezado: "Estado Nacimiento", valor: (e) => campo(e.estadoNacimientoId) },
  { encabezado: "Ciudad Nacimiento", valor: (e) => e.ciudadNacimientoId },
  { encabezado: "Nombre Padre", ancho: 22, valor: (e) => e.nombrePadre },
  { encabezado: "Nombre Madre", ancho: 22, valor: (e) => e.nombreMadre },
  // Nómina
  { encabezado: "Salario Diario", tipo: "dinero", valor: (e) => e.salarioDiario },
  { encabezado: "SBC", tipo: "dinero", valor: (e) => e.sbcParteFija },
  { encabezado: "SBC Variable", tipo: "dinero", valor: (e) => e.sbcParteVariable },
  { encabezado: "Salario Variable", tipo: "dinero", valor: (e) => e.salarioVariable },
  { encabezado: "Salario Promedio", tipo: "dinero", valor: (e) => e.salarioPromedio },
  { encabezado: "Fecha Salario Diario", tipo: "fecha", valor: (e) => fecha(e.fechaSalarioDiario) },
  { encabezado: "Fecha Salario Integrado", tipo: "fecha", valor: (e) => fecha(e.fechaSalarioIntegrado) },
  { encabezado: "Base Pago", valor: (e) => BASE[e.basePago] },
  { encabezado: "Base Cotizacion", valor: (e) => BASE[e.baseDeCotizacion] },
  // Catálogos. Los SAT van por descripción y no por clave: c_FormaPago es Number y "04" ≠ 4.
  { encabezado: "Tipo Contrato", ancho: 30, valor: (e) => campo(e.tipoContratoId, "Descripcion") },
  { encabezado: "Tipo Periodo", valor: (e) => campo(e.tipoPeriodoId) },
  { encabezado: "Tipo Jornada", valor: (e) => campo(e.tipoJornadaId, "Descripcion") },
  { encabezado: "Forma Pago", ancho: 22, valor: (e) => campo(e.formaDePagoId, "descripcion") },
  { encabezado: "Tipo Prestacion", valor: (e) => campo(e.tipoPrestacionId) },
  { encabezado: "Turno", valor: (e) => campo(e.turnoId) },
  { encabezado: "Regimen Fiscal", ancho: 30, valor: (e) => campo(e.regimenFiscalId, "descripcion") },
  { encabezado: "Tipo Regimen", ancho: 30, valor: (e) => campo(e.tipoRegimenId, "descripcion") },
  { encabezado: "Registro Patronal", valor: (e) => campo(e.registroPatronalId, "registroPatronalIMSS") },
  { encabezado: "Banco", valor: (e) => campo(e.bancoPagoElectronicoId) },
  { encabezado: "Sucursal", valor: (e) => e.sucursalPagoElectronico },
  { encabezado: "CLABE", ancho: 21, valor: (e) => e.clabeInterbancaria },
  { encabezado: "Cuenta Bancaria", valor: (e) => e.numCuenta },
  // IMSS / otros
  { encabezado: "UMF", valor: (e) => e.umf },
  { encabezado: "FONACOT", valor: (e) => e.numeroFONACOT },
  { encabezado: "AFORE", valor: (e) => e.afore },
  { encabezado: "Expediente", valor: (e) => e.expediente },
  { encabezado: "Sindicalizado", tipo: "siNo", valor: (e) => siNo(e.sindicalizado) },
  { encabezado: "Externo", tipo: "siNo", valor: (e) => siNo(e.externo) },
  // Sin valor guardado cuentan como SI: la nómina los lee con `?? true`.
  { encabezado: "Calculo PTU", tipo: "siNo", valor: (e) => siNo(e.calculoPTU !== false) },
  { encabezado: "Calculo Aguinaldo", tipo: "siNo", valor: (e) => siNo(e.calculoAguinaldo !== false) },
  { encabezado: "Extranjero Sin CURP", tipo: "siNo", valor: (e) => siNo(e.extranjeroSinCurp) },
  // Organización. El líder va por su Numero: el backend lo busca por `codigo` en la empresa destino.
  { encabezado: "Departamento", valor: (e) => campo(e.departamentoId) },
  { encabezado: "Puesto", valor: (e) => campo(e.puestoId) },
  { encabezado: "Lider Directo", valor: (e) => campo(e.liderDirectoId, "codigo") },
  // Contacto y domicilio. Municipio y Pais son informativos: el importador los saca del CP.
  { encabezado: "Correo", ancho: 26, valor: (e) => e.correoElectronico },
  { encabezado: "Telefono", valor: (e) => e.telefonos?.[0]?.numero },
  { encabezado: "Calle", ancho: 22, valor: (e) => e.calle },
  { encabezado: "Numero Ext", valor: (e) => e.numeroCalle },
  { encabezado: "Numero Int", valor: (e) => e.numeroInterior },
  { encabezado: "Colonia", ancho: 22, valor: (e) => e.colonia || campo(e.coloniaId) },
  { encabezado: "Municipio", ancho: 20, valor: (e) => campo(e.municipioId) },
  { encabezado: "Estado", valor: (e) => campo(e.estadoId) },
  { encabezado: "Pais", valor: (e) => campo(e.paisId) },
  { encabezado: "CP", valor: (e) => campo(e.coloniaId, "cp") },
];

export const ENCABEZADOS_PLANTILLA = COLUMNAS_PLANTILLA.map((c) => c.encabezado);

export const EJEMPLO_PLANTILLA = {
  "Numero": 1,
  "Nombre": "Juan",
  "Apellido Paterno": "García",
  "Apellido Materno": "López",
  "RFC": "GALJ800101HDF",
  "CURP": "GALJ800101HDFXXX01",
  "NSS": "12345678901",
  "Fecha Alta": "15/01/2020",
  "Fecha Reingreso": "",
  "Fecha Nacimiento": "01/01/1980",
  "Sexo": "Hombre",
  "Estado Civil": "Soltero",
  "Estado Nacimiento": "CDMX",
  "Ciudad Nacimiento": "Ciudad de México",
  "Nombre Padre": "Pedro García",
  "Nombre Madre": "María López",
  "Salario Diario": 250,
  "SBC": 268.75,
  "SBC Variable": 0,
  "Salario Variable": 0,
  "Salario Promedio": 0,
  "Fecha Salario Diario": "15/01/2020",
  "Fecha Salario Integrado": "15/01/2020",
  "Base Pago": "Sueldo",
  "Base Cotizacion": "Sueldo",
  "Tipo Contrato": "01",
  "Tipo Periodo": "Quincenal",
  "Tipo Jornada": "01",
  "Forma Pago": "04",
  "Tipo Prestacion": "Base",
  "Turno": "Matutino",
  "Regimen Fiscal": "612",
  "Tipo Regimen": "02",
  "Registro Patronal": "B0110100101",
  "Banco": "BBVA",
  "Sucursal": "",
  "CLABE": "123456789012345678",
  "Cuenta Bancaria": "",
  "UMF": "001",
  "FONACOT": "",
  "AFORE": "",
  "Expediente": "EXP001",
  "Sindicalizado": "NO",
  "Externo": "NO",
  "Calculo PTU": "SI",
  "Calculo Aguinaldo": "SI",
  "Extranjero Sin CURP": "NO",
  "Departamento": "Administración",
  "Puesto": "Contador",
  "Lider Directo": "",
  "Correo": "juan@empresa.com",
  "Telefono": "5512345678",
  "Calle": "Insurgentes Sur",
  "Numero Ext": "1234",
  "Numero Int": "",
  "Colonia": "Del Valle",
  "Municipio": "Benito Juárez",
  "Estado": "CDMX",
  "Pais": "México",
  "CP": "03100",
};

/** Fila de la plantilla (objeto encabezado → valor) a partir de un empleado poblado. */
export const empleadoAFila = (empleado) => {
  const fila = {};
  for (const col of COLUMNAS_PLANTILLA) fila[col.encabezado] = col.valor(empleado);
  return fila;
};

// Texto por default: NSS, CLABE, CP, UMF y compañía perderían ceros o dígitos como número
// (la CLABE tiene 18, más de lo que un double guarda exacto).
const celdaPlantilla = (col, valor) => {
  const vacio = valor === undefined || valor === null || valor === "";
  if (!vacio && (col.tipo === "dinero" || col.tipo === "entero")) {
    const numero = Number(valor);
    if (Number.isFinite(numero)) {
      return { v: numero, t: "n", s: col.tipo === "dinero" ? estiloDinero : numeroStyle };
    }
  }
  const estilo = ["fecha", "siNo", "centro"].includes(col.tipo) ? estiloCentro : textStyle;
  // Una celda "" cuenta como celda al releer, pero ninguna fila queda en blanco (Nombre y RFC
  // son obligatorios), así que se escribe para que todas lleven borde.
  return { v: vacio ? "" : String(valor), t: "s", s: estilo };
};

/**
 * Hoja "Empleados". El encabezado va en la fila 1, sin banda de título: el importador toma los
 * nombres de columna de la primera fila.
 */
export const construirHojaEmpleados = (filas) => {
  const encabezados = COLUMNAS_PLANTILLA.map((col) => ({ v: col.encabezado, t: "s", s: headerStyle }));
  const cuerpo = filas.map((fila) => COLUMNAS_PLANTILLA.map((col) => celdaPlantilla(col, fila[col.encabezado])));
  const hoja = XLSX.utils.aoa_to_sheet([encabezados, ...cuerpo]);
  hoja["!cols"] = COLUMNAS_PLANTILLA.map((col) => ({ wch: col.ancho || Math.max(col.encabezado.length + 2, 14) }));
  return hoja;
};

/** Hoja de referencia simple (catálogos de la plantilla): encabezados de las llaves del primer objeto. */
export const hojaTabla = (objetos) => {
  const encabezados = Object.keys(objetos[0] || {});
  const hoja = XLSX.utils.aoa_to_sheet([
    encabezados.map((h) => ({ v: h, t: "s", s: headerStyle })),
    ...objetos.map((obj) => encabezados.map((h) => ({ v: obj[h] ?? "", t: "s", s: textStyle }))),
  ]);
  hoja["!cols"] = encabezados.map((h) => ({
    wch: Math.min(60, Math.max(h.length + 2, ...objetos.map((obj) => String(obj[h] ?? "").length + 2), 12)),
  }));
  return hoja;
};

const estiloTitulo = {
  font: { bold: true, sz: 13, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "007A8A" } },
  alignment: { vertical: "center" },
};
const estiloSubtitulo = { font: { italic: true, color: { rgb: "595959" } } };
const estiloSeccion = {
  font: { bold: true, color: { rgb: "007A8A" } },
  fill: { fgColor: { rgb: "D6F4F8" } },
};
const estiloParrafo = { alignment: { wrapText: true, vertical: "top" } };

const INSTRUCCIONES = [
  ["Cómo importarlo", [
    "En la empresa destino entra a Empleados → Importar Empleados y sube este archivo. La hoja \"Empleados\" tiene que seguir siendo la primera del libro.",
    "Antes de importar, el importador te muestra los valores que no encontró en esa empresa; ahí se ve qué falta dar de alta.",
  ]],
  ["Lo que se busca por nombre en la empresa destino", [
    "Tipo Periodo, Turno, Tipo Prestacion, Banco (la cuenta de pago de nómina) y Registro Patronal son de cada empresa. Si en la empresa destino no existe uno con el mismo nombre, ese campo se queda vacío: dalo de alta antes o corrige la columna en este archivo.",
    "Departamento y Puesto se crean solos si no existen.",
    "El Lider Directo se busca por su Numero entre los empleados activos de la empresa destino.",
  ]],
  ["Lo que no se importa", [
    "Un empleado cuyo RFC ya tiene un empleado activo en la empresa destino (sale como repetido).",
    "Un empleado cuyo Numero ya usa otro empleado activo de la empresa destino (el aviso lista los números). Corrige la columna Numero y vuelve a subir el archivo completo: los que ya entraron se saltan solos por RFC.",
    "Los empleados deshabilitados se dan de alta como activos en la empresa destino.",
  ]],
  ["Lo que no viaja en este archivo", [
    "Documentos y archivos adjuntos, contactos de emergencia, teléfonos adicionales, conceptos de nómina (se asignan los automáticos de la empresa destino), cuentas contables, credenciales del reloj checador, historial de salarios y los datos de baja o finiquito.",
  ]],
];

/** Hoja "Instrucciones" del export. Va después de "Empleados" para no estorbarle al importador. */
export const hojaInstrucciones = ({ totalEmpleados, empresaNombre } = {}) => {
  const filas = [[{ v: "Empleados exportados para importar en otra empresa", t: "s", s: estiloTitulo }]];
  const partes = [
    empresaNombre ? `Empresa origen: ${empresaNombre}` : null,
    `Exportado el ${moment().format("DD/MM/YYYY")}`,
    totalEmpleados != null ? `${totalEmpleados} empleado(s)` : null,
  ].filter(Boolean);
  filas.push([{ v: partes.join(" · "), t: "s", s: estiloSubtitulo }]);
  for (const [seccion, parrafos] of INSTRUCCIONES) {
    filas.push([null]);
    filas.push([{ v: seccion, t: "s", s: estiloSeccion }]);
    for (const parrafo of parrafos) filas.push([{ v: `• ${parrafo}`, t: "s", s: estiloParrafo }]);
  }
  const hoja = XLSX.utils.aoa_to_sheet(filas);
  hoja["!cols"] = [{ wch: 120 }];
  hoja["!rows"] = [{ hpt: 24 }];
  return hoja;
};

/**
 * Libro con la hoja "Empleados" primero (el importador sólo lee `hojas[0]`) y después las hojas
 * extra: `[{ nombre, hoja }]`.
 */
export const construirLibroPlantilla = (filas, { hojasExtra = [] } = {}) => {
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, construirHojaEmpleados(filas), NOMBRE_HOJA_PLANTILLA);
  for (const { nombre, hoja } of hojasExtra) XLSX.utils.book_append_sheet(libro, hoja, nombre);
  return libro;
};

export const descargarLibroPlantilla = (libro, nombreArchivo) => XLSX.writeFile(libro, nombreArchivo);

/**
 * Aviso de `codigosRepetidos` de `empleados/importarEmpleados` (lo usan el importador normal y el
 * del software externo, que pegan al mismo endpoint). null si no hubo.
 */
export const textoCodigosRepetidos = (codigos) => {
  if (!codigos?.length) return null;
  const muestra = codigos.slice(0, 10).join(", ") + (codigos.length > 10 ? "…" : "");
  return codigos.length === 1
    ? `No se importó 1 empleado porque su Numero ya lo usa otro empleado activo: ${muestra}`
    : `No se importaron ${codigos.length} empleados porque su Numero ya lo usa otro empleado activo: ${muestra}`;
};
